/**
 * DashboardLayout -- shell for all authenticated staff pages.
 *
 * Redesign v2:
 *  - Letter-avatar sidebar navigation (matches Figma redesign)
 *  - Dynamic page title in top header derived from current route
 *  - SMS credit widget + user footer at sidebar bottom
 *  - All existing auth/org-gate/tour providers preserved
 */
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Search, Plus, LogOut, ChevronLeft } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/constants/routes'
import { APP_NAME } from '@/constants'
import { SmsCreditWidget } from '@/features/sms/components/SmsCreditWidget'
import { OrgSwitcherProvider } from '@/contexts/OrgSwitcherContext'
import { OrgSwitcher } from '@/features/platform-admin/OrgSwitcher'
import { OrgStatusPanel } from '@/pages/onboarding/PendingApprovalPage'
import { TourProvider } from '@/features/onboarding/TourContext'
import { TourOverlay } from '@/features/onboarding/TourOverlay'
import { RegisterVisitorDrawer } from '@/features/visitors/components/RegisterVisitorDrawer'
import type { OrgStatus } from '@/types'

// Nav items

interface NavItem {
  label:    string
  href:     string
  abbr:     string
  section?: string  // optional section header rendered before this item
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',         href: ROUTES.DASHBOARD,               abbr: 'D' },
  { label: 'Visitors',          href: ROUTES.VISITORS.LIST,            abbr: 'V' },
  { label: 'Pre-Registrations', href: ROUTES.PRE_REGISTRATIONS.LIST,  abbr: 'P' },
  { label: 'SMS',               href: ROUTES.SMS.LIST,                 abbr: 'S' },
  { label: 'Compliance',        href: ROUTES.COMPLIANCE.OVERVIEW,      abbr: 'C' },
  { label: 'Analytics',         href: ROUTES.ANALYTICS.OVERVIEW,       abbr: 'A' },
  { label: 'Audit Logs',        href: ROUTES.AUDIT_LOGS,               abbr: 'L' },
  { label: 'Settings',          href: ROUTES.SETTINGS.ROOT,            abbr: 'G' },
]

const SUPER_ADMIN_NAV_ITEMS: NavItem[] = [
  // ── Core ────────────────────────────────────────────────────────────────────
  { label: 'Platform',       href: ROUTES.PLATFORM_ADMIN.DASHBOARD,         abbr: 'PL', section: 'Core' },
  { label: 'Users',          href: ROUTES.PLATFORM_ADMIN.USERS,             abbr: 'US' },
  { label: 'Subscriptions',  href: ROUTES.PLATFORM_ADMIN.SUBSCRIPTIONS,     abbr: 'SB' },
  { label: 'Plans',          href: ROUTES.PLATFORM_ADMIN.PLANS,             abbr: 'PN' },
  { label: 'Requests',       href: ROUTES.PLATFORM_ADMIN.REQUESTS,          abbr: 'RQ' },
  { label: 'Transactions',   href: ROUTES.PLATFORM_ADMIN.SMS_TRANSACTIONS,  abbr: 'TX' },
  // ── Organisations ───────────────────────────────────────────────────────────
  { label: 'Organisations',  href: ROUTES.PLATFORM_ADMIN.ORGANIZATIONS,     abbr: 'OR', section: 'Organisations' },
  { label: 'Memberships',    href: ROUTES.PLATFORM_ADMIN.MEMBERSHIPS,       abbr: 'MB' },
  { label: 'Org Settings',   href: ROUTES.PLATFORM_ADMIN.ORG_SETTINGS,      abbr: 'OS' },
  { label: 'Org Payments',   href: ROUTES.PLATFORM_ADMIN.ORG_PAYMENTS,      abbr: 'OP' },
  { label: 'Kiosk Sessions', href: ROUTES.PLATFORM_ADMIN.KIOSK_SESSIONS,    abbr: 'KS' },
  { label: 'Invitations',    href: ROUTES.PLATFORM_ADMIN.INVITATIONS,       abbr: 'IV' },
  // ── Visitors ─────────────────────────────────────────────────────────────
  { label: 'Visit Purposes', href: ROUTES.PLATFORM_ADMIN.VISITOR_PURPOSES_ADMIN, abbr: 'VP', section: 'Visitors' },
  { label: 'Visitors',       href: ROUTES.PLATFORM_ADMIN.VISITORS_ADMIN,    abbr: 'VA' },
  { label: 'Visit Logs',     href: ROUTES.PLATFORM_ADMIN.VISIT_LOGS,        abbr: 'VL' },
  { label: 'Compliance',     href: ROUTES.COMPLIANCE.RULES,                 abbr: 'CP' },
  // ── SMS ──────────────────────────────────────────────────────────────────
  { label: 'SMS Credits',    href: ROUTES.PLATFORM_ADMIN.SMS_CREDITS,       abbr: 'CR', section: 'SMS' },
  { label: 'SMS Templates',  href: ROUTES.PLATFORM_ADMIN.SMS_TEMPLATES,     abbr: 'ST' },
  { label: 'SMS Messages',   href: ROUTES.PLATFORM_ADMIN.SMS_MESSAGES,      abbr: 'SM' },
  { label: 'SMS Campaigns',  href: ROUTES.PLATFORM_ADMIN.SMS_CAMPAIGNS,     abbr: 'SC' },
  // ── Auth ─────────────────────────────────────────────────────────────────
  { label: 'Reset Tokens',   href: ROUTES.PLATFORM_ADMIN.PASSWORD_RESET_TOKENS, abbr: 'RT', section: 'Auth' },
  { label: 'Refresh Tokens', href: ROUTES.PLATFORM_ADMIN.REFRESH_TOKENS,    abbr: 'RF' },
]

// Page title lookup

const PAGE_TITLE_MAP: Record<string, string> = {
  [ROUTES.DASHBOARD]:                      'Dashboard',
  [ROUTES.VISITORS.LIST]:                  'Visitors',
  [ROUTES.PRE_REGISTRATIONS.LIST]:         'Pre-Registrations',
  [ROUTES.SMS.LIST]:                       'SMS Log',
  [ROUTES.SMS.TEMPLATES]:                  'SMS Templates',
  [ROUTES.SMS.CAMPAIGNS]:                  'SMS Campaigns',
  [ROUTES.COMPLIANCE.OVERVIEW]:            'Compliance Events',
  [ROUTES.COMPLIANCE.RULES]:               'Compliance Rules',
  [ROUTES.ANALYTICS.OVERVIEW]:             'Analytics',
  [ROUTES.ANALYTICS.EXPORTS]:              'Analytics Exports',
  [ROUTES.AUDIT_LOGS]:                     'Audit Logs',
  [ROUTES.SETTINGS.ROOT]:                  'Settings',
  [ROUTES.SETTINGS.PROFILE]:               'My Profile',
  [ROUTES.SETTINGS.ORGANIZATION]:          'Organisation',
  [ROUTES.SETTINGS.MEMBERS]:               'Team Members',
  [ROUTES.SETTINGS.SUBSCRIPTION]:          'Subscription',
  [ROUTES.SETTINGS.BILLING]:               'Billing & Payments',
  [ROUTES.SETTINGS.VISITOR_PURPOSES]:      'Visit Purposes',
  [ROUTES.SETTINGS.WORKFLOWS]:             'Visitor Workflows',
  [ROUTES.SETTINGS.KIOSK]:                 'Kiosk Sessions',
  [ROUTES.SETTINGS.FIELD_CONFIG]:          'Form Fields',
  [ROUTES.PLATFORM_ADMIN.DASHBOARD]:       'Platform Console',
  [ROUTES.PLATFORM_ADMIN.ORGANIZATIONS]:   'Organisations',
  [ROUTES.PLATFORM_ADMIN.REQUESTS]:        'Payment Requests',
  [ROUTES.PLATFORM_ADMIN.PLANS]:           'Plans',
  [ROUTES.PLATFORM_ADMIN.SMS_TEMPLATES]:   'SMS Templates',
  [ROUTES.PLATFORM_ADMIN.SMS_MESSAGES]:    'SMS Messages',
  [ROUTES.PLATFORM_ADMIN.SMS_CAMPAIGNS]:   'SMS Campaigns',
  [ROUTES.PLATFORM_ADMIN.SMS_CREDITS]:     'SMS Credits',
  [ROUTES.PLATFORM_ADMIN.SMS_TRANSACTIONS]:    'SMS Transactions',
  [ROUTES.PLATFORM_ADMIN.USERS]:               'Users',
  [ROUTES.PLATFORM_ADMIN.MEMBERSHIPS]:         'Organisation Memberships',
  [ROUTES.PLATFORM_ADMIN.PASSWORD_RESET_TOKENS]: 'Password Reset Tokens',
  [ROUTES.PLATFORM_ADMIN.REFRESH_TOKENS]:      'Refresh Token Records',
  [ROUTES.PLATFORM_ADMIN.KIOSK_SESSIONS]:      'Kiosk Sessions',
  [ROUTES.PLATFORM_ADMIN.INVITATIONS]:         'Organisation Invitations',
  [ROUTES.PLATFORM_ADMIN.ORG_SETTINGS]:        'Organisation Settings',
  [ROUTES.PLATFORM_ADMIN.ORG_PAYMENTS]:        'Organisation Payments',
  [ROUTES.PLATFORM_ADMIN.SUBSCRIPTIONS]:       'Subscriptions',
  [ROUTES.PLATFORM_ADMIN.VISITOR_PURPOSES_ADMIN]: 'Visitor Purposes',
  [ROUTES.PLATFORM_ADMIN.VISIT_LOGS]:          'Visit Logs',
  [ROUTES.PLATFORM_ADMIN.VISITORS_ADMIN]:      'Visitors',
}

function getPageTitle(pathname: string): string {
  if (PAGE_TITLE_MAP[pathname]) return PAGE_TITLE_MAP[pathname]
  for (const [path, title] of Object.entries(PAGE_TITLE_MAP)) {
    if (pathname.startsWith(path + '/')) return title
  }
  return APP_NAME
}

const PAYMENT_POLL_INTERVAL_MS = 15_000

export function DashboardLayout() {
  const { user, organization, logout, role, refreshOrg } = useAuth()
  const navigate  = useNavigate()
  const location  = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [registerOpen, setRegisterOpen] = useState(false)
  // Lazy-mount: only render the drawer after first open so it doesn't fire
  // API calls (useVisitorPurposes) on every page before the user needs it.
  const [drawerMounted, setDrawerMounted] = useState(false)

  const isSuperAdmin = role === 'SUPER_ADMIN'
  const pageTitle    = getPageTitle(location.pathname)

  const handleLogout = async (): Promise<void> => {
    await logout()
    await navigate(ROUTES.AUTH.LOGIN, { replace: true })
  }

  const orgStatus  = organization?.status
  const isOrgBlocked =
    !isSuperAdmin &&
    orgStatus !== undefined &&
    orgStatus !== 'ACTIVE'

  useEffect(() => {
    if (isSuperAdmin || orgStatus !== 'PENDING_PAYMENT') return
    const id = setInterval(() => { void refreshOrg() }, PAYMENT_POLL_INTERVAL_MS)
    return () => { clearInterval(id) }
  }, [isSuperAdmin, orgStatus, refreshOrg])

  const tourEligible =
    !isSuperAdmin &&
    orgStatus === 'ACTIVE' &&
    (role === 'ORG_OWNER' || role === 'ORG_ADMIN')

  const userInitials = user
    ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase()
    : '?'

  return (
    <OrgSwitcherProvider>
      <TourProvider tourEligible={tourEligible}>
        <TourOverlay />
        {drawerMounted && (
          <RegisterVisitorDrawer open={registerOpen} onOpenChange={setRegisterOpen} />
        )}
        <div className="flex h-screen overflow-hidden bg-neutral-50">

          {/* Sidebar */}
          <aside
            className={cn(
              'flex flex-col bg-slate-950 text-slate-200 transition-all duration-200',
              collapsed ? 'w-16' : 'w-56',
            )}
          >
            {/* Brand */}
            <div className="flex h-[68px] items-center justify-between px-3">
              {!collapsed && (
                <div className="flex min-w-0 items-center gap-2.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-xs font-bold text-white">
                    E+
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold leading-tight text-white">
                      {APP_NAME}
                    </p>
                    <p className="truncate text-[11px] text-slate-400">
                      Visitor operations
                    </p>
                  </div>
                </div>
              )}
              {collapsed && (
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-xs font-bold text-white">
                  E+
                </div>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { setCollapsed(!collapsed) }}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                className="h-7 w-7 shrink-0 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
              >
                <ChevronLeft
                  className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')}
                />
              </Button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-2">
              {/* Org-scoped nav — hidden for SUPER_ADMIN who has no org context */}
              {!isSuperAdmin && NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.href === ROUTES.DASHBOARD}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-indigo-950/70 text-white'
                        : 'text-slate-400 hover:bg-slate-900 hover:text-slate-100',
                      collapsed && 'justify-center px-2',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div
                        className={cn(
                          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-bold',
                          isActive
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-800 text-slate-300',
                        )}
                      >
                        {item.abbr}
                      </div>
                      {!collapsed && <span>{item.label}</span>}
                    </>
                  )}
                </NavLink>
              ))}

              {/* SUPER_ADMIN platform nav — replaces org nav entirely */}
              {isSuperAdmin && (
                <>
                  {!collapsed && (
                    <div className="px-2 pb-1 pt-4">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                        Platform Admin
                      </p>
                    </div>
                  )}
                  {collapsed && <div className="mx-2 my-2 h-px bg-slate-800" />}
                  {SUPER_ADMIN_NAV_ITEMS.map((item) => (
                    <div key={item.href}>
                      {/* Section header — only when sidebar is expanded */}
                      {item.section && !collapsed && (
                        <p className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                          {item.section}
                        </p>
                      )}
                      {item.section && collapsed && (
                        <div className="mx-2 my-1.5 h-px bg-slate-800" />
                      )}
                      <NavLink
                        to={item.href}
                        end={item.href === ROUTES.PLATFORM_ADMIN.DASHBOARD}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-indigo-950/70 text-white'
                              : 'text-slate-400 hover:bg-slate-900 hover:text-slate-100',
                            collapsed && 'justify-center px-2',
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <div
                              className={cn(
                                'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[10px] font-bold',
                                isActive
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-slate-800 text-slate-300',
                              )}
                            >
                              {item.abbr}
                            </div>
                            {!collapsed && <span>{item.label}</span>}
                          </>
                        )}
                      </NavLink>
                    </div>
                  ))}
                </>
              )}
            </nav>

            {/* Sidebar footer: SMS balance + user */}
            <div className="border-t border-slate-800/60 p-3 space-y-3">
              {!collapsed && !isSuperAdmin && (
                <SmsCreditWidget />
              )}

              <div className={cn('flex items-center gap-2.5', collapsed && 'justify-center')}>
                <Link
                  to={isSuperAdmin ? ROUTES.PLATFORM_ADMIN.DASHBOARD : ROUTES.SETTINGS.PROFILE}
                  aria-label="Profile settings"
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white',
                    'bg-indigo-800 hover:bg-indigo-700 transition-colors',
                  )}
                >
                  {userInitials}
                </Link>
                {!collapsed && (
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold leading-tight text-white">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="truncate text-[11px] text-slate-400">{user?.email}</p>
                  </div>
                )}
                {!collapsed && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => { void handleLogout() }}
                    aria-label="Sign out"
                    className="h-7 w-7 shrink-0 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </aside>

          {/* Main content area */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">

            {/* Top header */}
            <header className="flex h-[68px] shrink-0 items-center justify-between border-b border-neutral-200 bg-white px-6">
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {isSuperAdmin ? 'Staidorf Consult' : (organization?.name ?? 'Workspace')}
                </p>
                <p className="text-base font-semibold text-foreground leading-tight">
                  {pageTitle}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <form
                  className="hidden lg:flex"
                  onSubmit={(e) => {
                    e.preventDefault()
                    const q = (e.currentTarget.elements.namedItem('q') as HTMLInputElement).value.trim()
                    if (q) navigate(`${ROUTES.SEARCH}?q=${encodeURIComponent(q)}`)
                  }}
                >
                  <div className="relative h-9 w-72">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      name="q"
                      type="search"
                      placeholder="Search everything..."
                      className="h-9 w-full rounded-lg border border-neutral-200 bg-neutral-50 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-300"
                    />
                  </div>
                </form>
                {!isSuperAdmin && (
                  <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={() => { setDrawerMounted(true); setRegisterOpen(true) }}
                  >
                    <Plus className="h-4 w-4" />
                    Register visitor
                  </Button>
                )}
                {isSuperAdmin && <OrgSwitcher />}
              </div>
            </header>

            {/* Page content */}
            <main className="flex-1 overflow-y-auto">
              <div className="p-6 md:p-8">
                {isOrgBlocked ? (
                  <OrgStatusPanel status={orgStatus as OrgStatus} />
                ) : (
                  <Outlet />
                )}
              </div>
            </main>
          </div>
        </div>
      </TourProvider>
    </OrgSwitcherProvider>
  )
}
